/**
 * Parser CSV para Z11 — carga masiva de llaveros.
 *
 * Formato esperado:
 * - Primera línea: encabezado obligatorio `identificador,serial_rfid`
 *   o `identificador;serial_rfid` (separador auto-detectado). La segunda
 *   columna acepta alias backward-compat (`serie`, `numero_serie`, etc.).
 * - Resto: filas con 2 campos. Espacios alrededor se trimean.
 *   Comillas dobles para escapar el separador (no necesario en este
 *   caso simple porque identificador/serial no llevan comas).
 *
 * Sprint 17 Z11. Excel chileno exporta CSV con `;` por default — por
 * eso aceptamos ambos. Sprint 19 S19-B1: el campo interno pasa a
 * `serial_rfid` (rename del modelo); se mantienen los alias de header.
 */

export interface FilaCSV {
  identificador: string;
  serial_rfid: string;
  /** Marcador para UI: índice (1-based) en el CSV original. */
  numero_linea: number;
}

export interface ResultadoParseo {
  filas: FilaCSV[];
  /** Errores estructurales (formato, encabezado, etc.). */
  errores: string[];
  /** Identificadores duplicados dentro del CSV (set de strings). */
  identificadores_duplicados: Set<string>;
  /** Números de serie duplicados dentro del CSV. */
  series_duplicadas: Set<string>;
  /** Separador detectado. */
  separador: ',' | ';';
}

function detectarSeparador(linea: string): ',' | ';' {
  // Cuenta separadores en la primera línea; gana el más frecuente.
  const comas = (linea.match(/,/g) ?? []).length;
  const punto_comas = (linea.match(/;/g) ?? []).length;
  return punto_comas > comas ? ';' : ',';
}

export function parsearCSV(texto: string): ResultadoParseo {
  const errores: string[] = [];
  const filas: FilaCSV[] = [];
  const identificadores_duplicados = new Set<string>();
  const series_duplicadas = new Set<string>();

  const lineas_crudas = texto
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lineas_crudas.length === 0) {
    errores.push('El CSV está vacío.');
    return {
      filas,
      errores,
      identificadores_duplicados,
      series_duplicadas,
      separador: ',',
    };
  }

  const separador = detectarSeparador(lineas_crudas[0]!);
  const partir = (l: string) =>
    l.split(separador).map((s) => s.trim().replace(/^"|"$/g, ''));

  // Validar encabezado.
  const encabezado = partir(lineas_crudas[0]!);
  if (encabezado.length !== 2) {
    errores.push(
      `Encabezado inválido. Se esperan 2 columnas separadas por '${separador}', se encontraron ${encabezado.length}.`,
    );
    return {
      filas,
      errores,
      identificadores_duplicados,
      series_duplicadas,
      separador,
    };
  }
  const col1 = encabezado[0]!.toLowerCase();
  const col2 = encabezado[1]!.toLowerCase();
  if (
    !['identificador', 'id', 'codigo', 'código'].includes(col1) ||
    ![
      'serial_rfid',
      'rfid',
      'numero_serie',
      'serie',
      'número de serie',
      'numero de serie',
    ].includes(col2)
  ) {
    errores.push(
      `Encabezado debe ser "identificador${separador}serial_rfid" (se encontró "${encabezado.join(separador)}").`,
    );
  }

  // Procesar filas de datos.
  const ids_vistos = new Set<string>();
  const series_vistas = new Set<string>();
  for (let i = 1; i < lineas_crudas.length; i++) {
    const linea = lineas_crudas[i]!;
    const campos = partir(linea);
    if (campos.length !== 2) {
      errores.push(
        `Línea ${i + 1}: se esperaban 2 columnas, se encontraron ${campos.length}.`,
      );
      continue;
    }
    const [identificador, serial_rfid] = campos;
    if (!identificador || !serial_rfid) {
      errores.push(`Línea ${i + 1}: identificador y número de serie son obligatorios.`);
      continue;
    }
    if (ids_vistos.has(identificador)) {
      identificadores_duplicados.add(identificador);
    } else {
      ids_vistos.add(identificador);
    }
    if (series_vistas.has(serial_rfid)) {
      series_duplicadas.add(serial_rfid);
    } else {
      series_vistas.add(serial_rfid);
    }
    filas.push({
      identificador,
      serial_rfid,
      numero_linea: i + 1,
    });
  }

  return {
    filas,
    errores,
    identificadores_duplicados,
    series_duplicadas,
    separador,
  };
}
